import { Component } from '@theme/component';

/**
 * Indéscents — configurateur de coffret.
 * 7 emplacements (3 × 50 ml + 4 × 20 ml) remplis via un sélecteur <dialog>.
 * Chaque emplacement alimente deux <input type="hidden" name="properties[…]" form="…"> :
 * le nom du parfum (visible) et son inspiration (préfixe « _ » = masquée côté client).
 */

// Nom réel de l'événement Horizon (StandardEvents.cartLinesUpdate dans @shopify/events),
// dispatché par <product-form-component> après un ajout au panier. On garde la chaîne
// littérale pour ne pas ajouter d'import au composant.
const CART_LINES_UPDATE = 'shopify:cart:lines-update';
const SCROLL_LOCK_CLASS = 'ind-picker-open';
// Événements publics (dispatchés sur document par la section « sélections » ou n'importe quel autre bloc).
const EVT_LOAD = 'ind:coffret:load';     // detail.titles = [7 titres, dans l'ordre des cases]
const EVT_FAMILY = 'ind:coffret:family'; // detail.fam = famille olfactive à pré-filtrer
const EVT_PICK = 'ind:coffret:pick';     // detail.title = parfum à placer dans la première case vide
const euros = (n) => `${new Intl.NumberFormat('fr-FR').format(Math.round(n))}\u00a0€`;

class IndescentsCoffretBuilder extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.formId = this.dataset.formId;
    this.total = parseInt(this.dataset.total || '7', 10);
    this.productId = this.dataset.productId || '';
    this.variantId = this.dataset.variantId || '';
    this.price = (this.dataset.price || '').trim();
    this.storageKey = `ind-coffret-${this.productId}`;
    this.slots = Array.from(this.querySelectorAll('button[data-slot]'));
    this.inputs = Array.from(this.querySelectorAll('input[type="hidden"][data-slot]'));
    this.inspInputs = Array.from(this.querySelectorAll('input[type="hidden"][data-slot-insp]'));
    this.removes = Array.from(this.querySelectorAll('button[data-remove]'));
    this.dialog = this.querySelector('dialog');
    this.grid = this.querySelector('[data-grid]');
    this.cards = Array.from(this.querySelectorAll('[data-pick]'));
    this.search = this.querySelector('[data-search]');
    this.filters = Array.from(this.querySelectorAll('[data-fam-filter]'));
    this.titleEl = this.querySelector('[data-dialog-title]');
    this.countEl = this.querySelector('[data-count]');
    this.barEl = this.querySelector('[data-bar]');
    this.msgEl = this.querySelector('[data-message]');
    this.origLine = this.querySelector('[data-orig-line]');
    // sélecteur : suivi des cases, message de progression, interstitiel
    this.tslots = Array.from(this.querySelectorAll('[data-tslot]'));
    this.tgroups = Array.from(this.querySelectorAll('[data-tgroup]'));
    this.hint = this.querySelector('[data-picker-hint]');
    this.stage = this.querySelector('[data-stage]');
    this.stageTitle = this.querySelector('[data-stage-title]');
    this.stageSub = this.querySelector('[data-stage-sub]');
    this.reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._busy = false;
    this.steps = Array.from(this.querySelectorAll('[data-step]'));
    this.stepStatus = this.querySelector('[data-step-status]');
    this.origTotalEl = this.querySelector('[data-orig-total]');
    this.sticky = this.querySelector('[data-sticky]');
    this.current = null;
    this.returnFocus = null;
    this.manualClose = false; // vrai si l'utilisateur a fermé le sélecteur (croix / Échap / fond) → pas d'enchaînement
    this.activeFam = '';

    this.slots.forEach((slot) => slot.addEventListener('click', () => this.onSlotClick(slot)));
    this.removes.forEach((btn) => btn.addEventListener('click', () => this.onRemoveClick(btn)));
    this.cards.forEach((card) => card.addEventListener('click', () => this.pick(card)));
    this.querySelector('[data-close]')?.addEventListener('click', () => this.closeDialog(true));
    this.querySelector('[data-random]')?.addEventListener('click', () => this.randomFill());
    this.querySelector('[data-reset]')?.addEventListener('click', () => this.reset());
    this.search?.addEventListener('input', () => this.applyFilters());
    this.emptyEl = this.querySelector('[data-empty]');
    this.querySelector('[data-clear-filters]')?.addEventListener('click', () => {
      if (this.search) this.search.value = '';
      const all = this.filters.find((x) => x.dataset.famFilter === '');
      if (all) this.setFilter(all); else this.applyFilters();
      this.search?.focus({ preventScroll: true });
    });
    this.filters.forEach((f) => f.addEventListener('click', () => this.setFilter(f)));
    this.dialog?.addEventListener('click', (e) => { if (e.target === this.dialog) this.closeDialog(true); });
    this.dialog?.addEventListener('cancel', () => { this.manualClose = true; }); // Échap
    this.dialog?.addEventListener('close', () => this.onDialogClose());

    this.submitGuard = (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      const isMain = form.id === this.formId;
      const vid = form.querySelector('input[name="id"]')?.value;
      if (!isMain && !(vid && vid === this.variantId)) return;
      if (this.filledCount() >= this.total) {
        // formulaire secondaire (barre collante…) : y recopier les 7 choix (+ inspirations)
        if (!isMain) [...this.inputs, ...this.inspInputs].forEach((i) => {
          let clone = form.querySelector(`input[name="${CSS.escape(i.name)}"]`);
          if (!clone) { clone = document.createElement('input'); clone.type = 'hidden'; clone.name = i.name; form.appendChild(clone); }
          clone.value = i.value;
        });
        return;
      }
      e.preventDefault(); e.stopImmediatePropagation();
      this.nudge();
    };
    document.addEventListener('submit', this.submitGuard, true);
    // Horizon déclenche l'ajout au panier sur le clic du bouton : on bloque aussi le clic en phase de capture.
    this.clickGuard = (e) => {
      const btn = e.target instanceof Element ? e.target.closest('.add-to-cart-button, button[type="submit"]') : null;
      if (!btn) return;
      const form = btn.form || btn.closest('form');
      if (!form) return;
      const vid = form.querySelector('input[name="id"]')?.value;
      if (form.id !== this.formId && !(vid && vid === this.variantId)) return;
      if (this.filledCount() < this.total) {
        e.preventDefault(); e.stopImmediatePropagation();
        this.nudge();
      }
    };
    document.addEventListener('click', this.clickGuard, true);

    // Après un ajout réussi de CE produit : on vide le coffret pour en composer un autre.
    this.onCartUpdate = (e) => {
      if (e.action !== 'add') return;
      const lines = Array.isArray(e.lines) ? e.lines : [];
      const hitLine = lines.some((l) => this.sameVariant(l?.merchandiseId));
      const done = (detail) => {
        if (detail?.didError) return;
        const hitProduct = detail?.productId != null && String(detail.productId) === this.productId;
        if (hitLine || hitProduct) this.afterAdded();
      };
      if (e.promise && typeof e.promise.then === 'function') {
        e.promise.then((result) => done(result?.detail)).catch(() => { /* ajout échoué : on garde le coffret */ });
      } else if (hitLine) {
        done(null);
      }
    };
    document.addEventListener(CART_LINES_UPDATE, this.onCartUpdate);

    // Sélections prêtes à l'emploi + accès par famille (section « Pars d'une sélection »).
    this.onLoad = (e) => { if (Array.isArray(e.detail?.titles)) this.loadPreset(e.detail.titles); };
    this.onFamily = (e) => { if (e.detail?.fam) this.openWithFamily(e.detail.fam); };
    document.addEventListener(EVT_LOAD, this.onLoad);
    document.addEventListener(EVT_FAMILY, this.onFamily);
    // Un flacon cliqué ailleurs sur la page (bandeau des 55 parfums) : on le met dans la première case vide.
    this.onPick = (e) => { if (e.detail?.title) this.pickByTitle(e.detail.title); };
    document.addEventListener(EVT_PICK, this.onPick);
    // Tout lien « #composer » de la page (boutons des sections) : on remonte au configurateur et on ouvre la première case vide.
    this.onAnchor = (e) => {
      const a = e.target instanceof Element ? e.target.closest('a[href="#composer"], a[href$="#composer"]') : null;
      if (!a) return;
      e.preventDefault();
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const empty = this.slots.find((s) => !s.dataset.value);
      if (empty) this.openFor(empty); else this.flash('Coffret complet — tu peux l’ajouter au panier.', true);
    };
    document.addEventListener('click', this.onAnchor);

    this.armPickerImages();
    this.initSticky();
    this.restore();
    this.update();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('submit', this.submitGuard, true);
    document.removeEventListener('click', this.clickGuard, true);
    document.removeEventListener(CART_LINES_UPDATE, this.onCartUpdate);
    document.removeEventListener(EVT_LOAD, this.onLoad);
    document.removeEventListener(EVT_FAMILY, this.onFamily);
    document.removeEventListener(EVT_PICK, this.onPick);
    document.removeEventListener('click', this.onAnchor);
    this.observer?.disconnect();
    this.sticky?.remove();
    this.unlockScroll();
  }

  /**
   * Perf : les 55 vignettes du sélecteur ne portent pas de `src` au rendu (la fenêtre est fermée au chargement).
   * On les charge pendant un temps mort du navigateur — et immédiatement si le sélecteur s'ouvre avant.
   */
  armPickerImages() {
    this.loadPickerImages = () => {
      this.querySelectorAll('.ind-pick img[data-src]').forEach((img) => {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      });
    };
    if (typeof requestIdleCallback === 'function') this._idle = requestIdleCallback(() => this.loadPickerImages(), { timeout: 2500 });
    else this._idle = setTimeout(() => this.loadPickerImages(), 1500);
  }

  /* ---------- helpers ---------- */

  sameVariant(id) {
    if (id == null || !this.variantId) return false;
    return String(id).split('/').pop() === this.variantId; // accepte "123" ou "gid://shopify/ProductVariant/123"
  }

  missingLabel() {
    const missing = this.total - this.filledCount();
    return `${missing} parfum${missing > 1 ? 's' : ''}`;
  }

  /** Tentative d'ajout avec un coffret incomplet : message + ouverture de la première case vide. */
  nudge() {
    this.flash(`Il te reste ${this.missingLabel()} à choisir.`);
    const empty = this.slots.find((s) => !s.dataset.value);
    if (empty) { empty.scrollIntoView({ behavior: 'smooth', block: 'center' }); this.openFor(empty); }
  }

  lockScroll() { document.documentElement.classList.add(SCROLL_LOCK_CLASS); }
  unlockScroll() { document.documentElement.classList.remove(SCROLL_LOCK_CLASS); }

  /* ---------- cases ---------- */

  onSlotClick(slot) { this.openFor(slot); }

  onRemoveClick(btn) {
    const slot = this.slots.find((s) => s.dataset.slot === btn.dataset.for);
    if (!slot) return;
    this.clear(slot);
    slot.focus(); // la croix disparaît : on garde le focus sur la case
  }

  /* ---------- sélecteur ---------- */

  /** @param {boolean} [auto] ouverture automatique (enchaînement) : ne réinitialise pas le drapeau de fermeture manuelle */
  openFor(slot, auto = false) {
    if (!this.dialog) return;
    if (!auto) this.manualClose = false;
    this.current = slot;
    this.returnFocus = slot;
    if (this.titleEl) this.titleEl.textContent = this.titleFor(slot);
    if (!auto) {
      // ouverture manuelle : on repart de la liste complète ; en enchaînement on garde recherche et famille
      if (this.search) this.search.value = '';
      this.activeFam = ''; this.filters.forEach((x) => this.markFilter(x, x.dataset.famFilter === ''));
      const w = this.slotWords(slot);
      this.setHint(slot.dataset.value
        ? `Cette case contient déjà <strong>${slot.dataset.value}</strong> — choisis un autre parfum pour le remplacer.`
        : `Choisis ${w.poss} ${w.ordWord} ${w.kind} <span class="ind-picker__hint-dim">(${w.fmt} ml)</span> — par nom, inspiration ou famille.`, false);
    }
    this.applyFilters();
    this.renderTracker();
    this.markCards();
    this.loadPickerImages?.();
    if (!this.dialog.open) { this.lockScroll(); this.dialog.showModal(); }
    if (this.grid) this.grid.scrollTop = 0;
    if (!auto) this.search?.focus({ preventScroll: true });
  }

  closeDialog(manual) {
    if (manual) this.manualClose = true;
    if (this.dialog?.open) this.dialog.close();
  }

  /** Événement `close` du <dialog> : couvre la croix, le fond, Échap et les fermetures programmatiques. */
  onDialogClose() {
    if (this.dialog?.open) return; // rouvert dans la foulée (enchaînement) : rien à défaire
    this.unlockScroll();
    this.current = null;
    if (this.stage) this.stage.hidden = true;
    const target = this.returnFocus; this.returnFocus = null;
    if (target?.isConnected) target.focus();
  }

  setFilter(f) {
    this.activeFam = f.dataset.famFilter;
    this.filters.forEach((x) => this.markFilter(x, x === f));
    this.applyFilters();
  }

  markFilter(x, on) {
    x.classList.toggle('is-active', on);
    x.setAttribute('aria-pressed', on ? 'true' : 'false');
  }

  async pick(card) {
    if (this._busy) return;
    const slot = this.current || this.slots.find((s) => !s.dataset.value);
    if (!slot) return;
    this._busy = true;
    card.classList.remove('is-picking'); void card.offsetWidth; card.classList.add('is-picking');
    this.fill(slot, card.dataset);
    this.renderTracker();
    this.markCards();
    await this.fly(card, slot);
    const w = this.slotWords(slot);
    const next = this.slots.find((s) => !s.dataset.value);
    if (!next) {
      this.setHint(`<strong>✓ ${card.dataset.title}</strong> → ${w.fmt} ml n°${w.n}. Ton coffret est complet.`);
      await this.showStage('Ton coffret est complet', `Les ${this.total} parfums sont choisis — il ne reste qu’à l’ajouter au panier.`, 1600);
      this._busy = false; this.current = null;
      this.closeDialog(false);
      this.celebrate();
      return;
    }
    if (this.manualClose) { this._busy = false; this.current = null; this.closeDialog(false); return; }
    const groupChange = slot.dataset.slot.split('-')[0] !== next.dataset.slot.split('-')[0];
    if (groupChange) {
      const n50 = this.slots.filter((s) => s.dataset.slot.startsWith('50-')).length;
      const n20 = this.slots.filter((s) => s.dataset.slot.startsWith('20-')).length;
      await this.showStage(`${n50} grands flacons choisis`, `Place aux ${n20} découvertes en 20 ml — pour tester, offrir, voyager.`, 1700);
    }
    // Enchaînement : on passe à la case vide suivante, en gardant recherche et famille.
    this.openFor(next, true);
    const nw = this.slotWords(next);
    this.setHint(`<strong>✓ ${card.dataset.title}</strong> → ${w.fmt} ml n°${w.n}. À toi : ${nw.poss} ${nw.ordWord} ${nw.kind} <span class="ind-picker__hint-dim">(${nw.fmt} ml)</span>.`);
    this._busy = false;
  }

  /* ---------- aides au sélecteur (texte, suivi, animations) ---------- */

  slotWords(slot) {
    const [fmt, ns] = slot.dataset.slot.split('-');
    const n = parseInt(ns, 10);
    const fem = fmt !== '50';
    const ords = fem ? ['première', 'deuxième', 'troisième', 'quatrième', 'cinquième', 'sixième', 'septième', 'huitième']
                     : ['premier', 'deuxième', 'troisième', 'quatrième', 'cinquième', 'sixième'];
    const total = this.slots.filter((s) => s.dataset.slot.startsWith(`${fmt}-`)).length;
    return { fmt, n, total, kind: fem ? 'découverte' : 'grand flacon', poss: fem ? 'ta' : 'ton', ordWord: ords[n - 1] || `n°${n}` };
  }

  titleFor(slot) {
    const w = this.slotWords(slot);
    return w.fmt === '50' ? `Grand flacon ${w.n}/${w.total} · 50 ml` : `Découverte ${w.n}/${w.total} · 20 ml`;
  }

  setHint(html, flash = true) {
    if (!this.hint) return;
    this.hint.innerHTML = html;
    if (flash && !this.reduced) { this.hint.classList.remove('is-flash'); void this.hint.offsetWidth; this.hint.classList.add('is-flash'); }
  }

  /** Les 7 pastilles en haut du sélecteur : remplies (miniature + ✓), en cours (anneau), groupe actif, panier prêt. */
  renderTracker() {
    if (!this.tslots.length) return;
    const complete = this.filledCount() >= this.total;
    this.tslots.forEach((t) => {
      const key = t.dataset.tslot;
      if (key === 'cart') { t.classList.toggle('is-ready', complete); return; }
      const slot = this.slots.find((s) => s.dataset.slot === key);
      const filled = !!slot?.dataset.value;
      const src = slot?.querySelector('[data-slot-img]')?.getAttribute('src');
      const img = t.querySelector('img');
      if (img && filled && src && img.getAttribute('src') !== src) img.src = src;
      t.classList.toggle('is-filled', filled);
      t.classList.toggle('is-current', !!this.current && this.current.dataset.slot === key);
    });
    const cur = this.current?.dataset.slot.split('-')[0] || '';
    this.tgroups.forEach((g) => g.classList.toggle('is-active', g.dataset.tgroup === cur));
  }

  /** Cartes déjà dans le coffret : badge « Dans ton coffret · 50 ml n°1 » ; carte de la case courante : contour. */
  markCards() {
    this.cards.forEach((c) => {
      const inSlot = this.slots.find((s) => s.dataset.value === c.dataset.title);
      c.classList.toggle('is-in', !!inSlot);
      const b = c.querySelector('[data-pick-in]');
      if (b) b.textContent = inSlot ? `Dans ton coffret · ${inSlot.dataset.slot.replace('-', ' ml n°')}` : '';
      c.classList.toggle('is-selected', !!this.current && c.dataset.title === this.current.dataset.value);
    });
  }

  /** Interstitiel dans le sélecteur (changement d'étape, coffret complet) ; un clic passe. */
  showStage(title, sub, ms) {
    return new Promise((resolve) => {
      if (!this.stage) { resolve(); return; }
      if (this.stageTitle) this.stageTitle.textContent = title;
      if (this.stageSub) this.stageSub.textContent = sub;
      this.stage.hidden = false;
      const done = () => { clearTimeout(this._stageT); this.stage.hidden = true; this.stage.removeEventListener('click', done); resolve(); };
      this._stageT = setTimeout(done, this.reduced ? 900 : ms);
      this.stage.addEventListener('click', done);
    });
  }

  /** Le flacon « vole » de la carte vers sa pastille dans le suivi. */
  fly(card, slot) {
    return new Promise((resolve) => {
      const img = card.querySelector('img');
      const target = this.tslots.find((t) => t.dataset.tslot === slot.dataset.slot);
      if (this.reduced || !img || !target || !img.currentSrc || typeof img.animate !== 'function') { resolve(); return; }
      const a = img.getBoundingClientRect(); const b = target.getBoundingClientRect();
      if (!a.width || !b.width) { resolve(); return; }
      const ghost = document.createElement('img');
      ghost.src = img.currentSrc; ghost.alt = ''; ghost.className = 'ind-fly';
      Object.assign(ghost.style, { left: `${a.left}px`, top: `${a.top}px`, width: `${a.width}px`, height: `${a.height}px` });
      (this.dialog || document.body).appendChild(ghost); // dans le <dialog> : au-dessus de la couche modale
      const dx = b.left + b.width / 2 - (a.left + a.width / 2);
      const dy = b.top + b.height / 2 - (a.top + a.height / 2);
      const sc = b.width / a.width;
      const anim = ghost.animate(
        [{ transform: 'translate(0,0) scale(1)', opacity: 1 }, { transform: `translate(${dx}px,${dy}px) scale(${sc})`, opacity: 0.85 }],
        { duration: 520, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'forwards' }
      );
      const end = () => { ghost.remove(); target.classList.remove('is-pop'); void target.offsetWidth; target.classList.add('is-pop'); resolve(); };
      anim.onfinish = end; anim.oncancel = end;
      setTimeout(end, 900); // filet de sécurité
    });
  }

  /** Coffret complet : on remonte à l'étape 3 et on fait respirer le bouton d'ajout. */
  celebrate() {
    const step = this.steps.find((s) => s.dataset.step === 'atc') || this;
    step.scrollIntoView({ behavior: this.reduced ? 'auto' : 'smooth', block: 'center' });
    const btn = document.getElementById(this.formId)?.querySelector('.add-to-cart-button');
    if (btn && !this.reduced) { btn.classList.remove('ind-builder__atc--ready'); void btn.offsetWidth; btn.classList.add('ind-builder__atc--ready'); setTimeout(() => btn.classList.remove('ind-builder__atc--ready'), 2600); }
    this.flash('Coffret complet — tu peux l’ajouter au panier.', true);
  }

  fill(slot, d) {
    slot.dataset.value = d.title;
    slot.dataset.orig = d.orig || '';
    slot.classList.add('is-filled');
    slot.closest('.ind-slot-wrap')?.classList.add('is-filled');
    slot.querySelector('[data-slot-img]').src = d.img;
    slot.querySelector('[data-slot-name]').textContent = d.title;
    slot.querySelector('[data-slot-insp]').textContent = d.insp ? `Inspiré de ${d.insp}` : '';
    const input = this.inputs.find((i) => i.dataset.slot === slot.dataset.slot);
    if (input) input.value = d.title;
    const insp = this.inspInputs.find((i) => i.dataset.slotInsp === slot.dataset.slot);
    if (insp) insp.value = d.insp || '';
    this.update();
  }

  clear(slot) {
    delete slot.dataset.value;
    delete slot.dataset.orig;
    slot.classList.remove('is-filled');
    slot.closest('.ind-slot-wrap')?.classList.remove('is-filled');
    slot.querySelector('[data-slot-img]')?.removeAttribute('src');
    const input = this.inputs.find((i) => i.dataset.slot === slot.dataset.slot);
    if (input) input.value = '';
    const insp = this.inspInputs.find((i) => i.dataset.slotInsp === slot.dataset.slot);
    if (insp) insp.value = '';
    this.update();
  }

  reset() { this.slots.forEach((s) => this.clear(s)); }

  /** Coffret ajouté au panier : on repart de zéro. */
  afterAdded() {
    this.manualClose = false;
    this.closeDialog(false);
    this.reset();
    try { sessionStorage.removeItem(this.storageKey); } catch (e) { /* stockage indisponible */ }
    this.flash('Coffret ajouté au panier — tu peux en composer un autre.', true);
  }

  randomFill() {
    const pool = this.cards.map((c) => c.dataset);
    const used = new Set(this.slots.map((s) => s.dataset.value).filter(Boolean));
    this.slots.filter((s) => !s.dataset.value).forEach((slot) => {
      const candidates = pool.filter((d) => !used.has(d.title));
      const d = candidates[Math.floor(Math.random() * candidates.length)];
      if (d) { used.add(d.title); this.fill(slot, d); }
    });
    this.flash('Coffret composé au hasard — change ce que tu veux.', true);
  }

  filledCount() { return this.slots.filter((s) => s.dataset.value).length; }

  /** Somme des prix (chez leurs maisons) des originaux dont s'inspirent les parfums choisis. */
  origTotal() {
    return this.slots.reduce((sum, s) => sum + (s.dataset.value ? parseFloat(s.dataset.orig) || 0 : 0), 0);
  }

  /* ---------- sélections prêtes à l'emploi ---------- */

  /**
   * Remplit les 7 cases dans l'ordre à partir de titres de parfums (3 × 50 ml puis 4 × 20 ml).
   * Les titres inconnus laissent la case vide.
   */
  loadPreset(titles) {
    this.manualClose = false;
    this.closeDialog(false);
    this.reset();
    let filled = 0;
    this.slots.forEach((slot, i) => {
      const title = titles[i];
      const card = title ? this.cards.find((c) => c.dataset.title === title) : null;
      if (card) { this.fill(slot, card.dataset); filled += 1; }
    });
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const missing = this.total - filled;
    this.flash(missing > 0
      ? `Sélection chargée — il te reste ${missing} case${missing > 1 ? 's' : ''} à remplir.`
      : 'Sélection chargée — remplace ce que tu veux, puis ajoute au panier.', true);
  }

  /** Place un parfum (par titre) dans la première case vide ; coffret plein → message. */
  pickByTitle(title) {
    const card = this.cards.find((c) => c.dataset.title === title);
    if (!card) return;
    const slot = this.slots.find((s) => !s.dataset.value);
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (!slot) { this.flash('Coffret complet — retire un parfum pour le remplacer.', false); return; }
    this.fill(slot, card.dataset);
    this.flash(`${title} ajouté en ${slot.dataset.label.toLowerCase().replace('parfum ', '')}.`, true);
  }

  /** Ouvre le sélecteur sur la première case vide, pré-filtré sur une famille olfactive. */
  openWithFamily(fam) {
    const slot = this.slots.find((s) => !s.dataset.value) || this.slots[0];
    if (!slot) return;
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.openFor(slot);
    const f = this.filters.find((x) => x.dataset.famFilter === fam);
    if (f) this.setFilter(f);
  }

  /* ---------- barre collante (mobile et desktop, quand le configurateur est hors écran) ---------- */

  initSticky() {
    if (!this.sticky || typeof IntersectionObserver === 'undefined') return;
    // Sortie du flux du produit (colonne collante, transformations…) : la barre vit directement dans <body>.
    document.body.appendChild(this.sticky);
    this.stickyStatus = this.sticky.querySelector('[data-sticky-status]');
    this.stickyBtn = this.sticky.querySelector('[data-sticky-btn]');
    this.stickyBtn?.addEventListener('click', () => this.onStickyClick());
    this.observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      this.sticky.hidden = entry.isIntersecting;
      this.sticky.classList.toggle('is-visible', !entry.isIntersecting);
    }, { rootMargin: '-80px 0px 0px 0px', threshold: 0 });
    this.observer.observe(this);
  }

  onStickyClick() {
    if (this.filledCount() >= this.total) {
      const btn = document.getElementById(this.formId)?.querySelector('.add-to-cart-button');
      if (btn) { btn.click(); return; }
    }
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const empty = this.slots.find((s) => !s.dataset.value);
    if (empty) this.openFor(empty);
  }

  syncSticky(n, missing) {
    if (!this.sticky) return;
    if (this.stickyStatus) this.stickyStatus.textContent = missing > 0 ? `${n}/${this.total} choisis` : 'Coffret complet';
    if (this.stickyBtn) {
      const price = this.price ? ` — ${this.price}` : '';
      this.stickyBtn.textContent = missing > 0 ? `Composer${price}` : `Ajouter au panier${price}`;
      this.stickyBtn.classList.toggle('is-ready', missing === 0);
    }
  }

  /* ---------- état global / formulaire ---------- */

  update() {
    const n = this.filledCount();
    const missing = this.total - n;
    const waiting = missing > 0;
    if (this.countEl) this.countEl.textContent = String(n);
    if (this.barEl) this.barEl.style.width = `${Math.round((n / this.total) * 100)}%`;
    this.classList.toggle('is-complete', !waiting);
    this.syncSticky(n, missing);
    this.syncSteps(waiting);
    if (this.dialog?.open) { this.renderTracker(); this.markCards(); }

    // Valeur des originaux qui ont inspiré les parfums choisis (métachamp custom.prix_original).
    if (this.origLine) {
      const total = this.origTotal();
      this.origLine.hidden = !(n > 0 && total > 0);
      if (this.origTotalEl) this.origTotalEl.textContent = `≈ ${euros(total)}`;
    }

    const form = document.getElementById(this.formId);
    const btn = form?.querySelector('.add-to-cart-button');
    if (btn) this.syncButton(btn, waiting, missing);

    // Paiement accéléré (Shop Pay, PayPal…) : masqué tant que le coffret est incomplet.
    const accel = form?.closest('product-form-component')
      ?.querySelector('.accelerated-checkout-block, [class*="accelerated-checkout"], shopify-accelerated-checkout');
    if (accel) {
      if (accel.dataset.indWasHidden === undefined) accel.dataset.indWasHidden = accel.hidden ? '1' : '0';
      accel.hidden = waiting || accel.dataset.indWasHidden === '1';
    }

    if (this.msgEl && !waiting && !this.msgEl.dataset.flash) this.msgEl.textContent = 'Coffret complet — tu peux l’ajouter au panier.';
    else if (this.msgEl && !this.msgEl.dataset.flash) this.msgEl.textContent = ''; // les étapes numérotées disent déjà quoi faire
    this.save();
  }

  /** Étapes numérotées : compteur par format, étape en cours, étape terminée, case suivante mise en avant. */
  syncSteps(waiting) {
    let currentSet = false;
    this.steps.forEach((step) => {
      const key = step.dataset.step;
      if (key === 'atc') {
        step.classList.toggle('is-current', !waiting);
        step.classList.remove('is-done');
        if (this.stepStatus) this.stepStatus.textContent = waiting ? 'En attente' : 'Prêt ✓';
        return;
      }
      const mine = this.slots.filter((s) => s.dataset.slot.startsWith(`${key}-`));
      const filled = mine.filter((s) => s.dataset.value).length;
      const done = mine.length > 0 && filled === mine.length;
      const strong = step.querySelector('[data-step-count] strong');
      if (strong) strong.textContent = String(filled);
      step.classList.toggle('is-done', done);
      const current = !done && !currentSet;
      step.classList.toggle('is-current', current);
      if (current) currentSet = true;
    });
    const next = this.slots.find((s) => !s.dataset.value);
    this.slots.forEach((s) => s.classList.toggle('is-next', s === next));
  }

  /** Nœud portant le libellé du bouton d'ajout (nœud texte nu dans Horizon, span dans la barre collante). */
  labelNode(btn) {
    return btn.querySelector('.add-to-cart-text__content, .add-to-cart-text')
      || Array.from(btn.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim())
      || null;
  }

  syncButton(btn, waiting, missing) {
    const node = this.labelNode(btn);
    if (node && btn.dataset.indLabel === undefined) btn.dataset.indLabel = node.textContent.trim();
    btn.classList.toggle('ind-builder__atc--waiting', waiting);
    btn.setAttribute('aria-describedby', 'ind-builder-message');
    if (waiting) btn.setAttribute('aria-disabled', 'true'); else btn.removeAttribute('aria-disabled');
    if (!node) return;
    const ready = this.price ? `Ajouter le coffret — ${this.price}` : (btn.dataset.indLabel || 'Ajouter le coffret');
    const label = waiting ? `Choisis encore ${missing} parfum${missing > 1 ? 's' : ''}` : ready;
    if (node.textContent !== label) node.textContent = label;
  }

  applyFilters() {
    const q = (this.search?.value || '').trim().toLowerCase();
    this.cards.forEach((c) => {
      const hay = `${c.dataset.title} ${c.dataset.insp} ${c.dataset.fam} ${c.dataset.genre}`.toLowerCase();
      const okQ = !q || hay.includes(q);
      const okF = !this.activeFam || c.dataset.fam === this.activeFam;
      c.hidden = !(okQ && okF);
    });
    if (this.emptyEl) this.emptyEl.hidden = this.cards.some((c) => !c.hidden);
  }

  flash(text, soft) {
    if (!this.msgEl) return;
    this.msgEl.textContent = text; this.msgEl.dataset.flash = '1';
    this.msgEl.classList.toggle('is-error', !soft);
    clearTimeout(this._t);
    this._t = setTimeout(() => { delete this.msgEl.dataset.flash; this.msgEl.classList.remove('is-error'); this.update(); }, 3500);
  }

  save() {
    try {
      const data = this.slots.map((s) => s.dataset.value ? { slot: s.dataset.slot, title: s.dataset.value } : null);
      if (data.some(Boolean)) sessionStorage.setItem(this.storageKey, JSON.stringify(data));
      else sessionStorage.removeItem(this.storageKey);
    } catch (e) { /* stockage indisponible */ }
  }

  restore() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
      if (!Array.isArray(saved)) return;
      saved.forEach((entry) => {
        if (!entry) return;
        const slot = this.slots.find((s) => s.dataset.slot === entry.slot);
        const card = this.cards.find((c) => c.dataset.title === entry.title);
        if (slot && card) this.fill(slot, card.dataset);
      });
    } catch (e) { /* ignore */ }
  }
}

if (!customElements.get('indescents-coffret-builder')) customElements.define('indescents-coffret-builder', IndescentsCoffretBuilder);
